import React, { useEffect, useRef } from "react";
import ElementFormPresetFields from "../elements/ElementFormPresetFields";
import ElementFormCustomFields from "../elements/ElementFormCustomFields";
import ElementFormConsentFields from "../elements/ElementFormConsentFields";
import ElementFormButton from "../elements/ElementFormButton";
import { getWebsiteIntegration } from "../../utils/builderUtils";
import { splitConsentPlacements } from "../../utils/formConsentFields";
import { buildFormStylesheet } from "./ElementFormStyleResolver";
import BuilderStyleTag from "../shared/BuilderStyleTag";

const RecaptchaDiv = React.forwardRef((props, ref) => {
  const {
    builder,
    element,
    website,
  } = props;
  const recaptchaVersion = builder
    ? getWebsiteIntegration(website, "Googlerecaptcha")?.api_data?.["key_version"] || "v2"
    : (typeof app !== "undefined" && app?.recaptcha_key_version) || "v2";

  if (element.options["check_recaptcha"] !== "true") {
    return null;
  }

  return (
    <>
      {recaptchaVersion === "v2" && (
        <div ref={ref} className="cf-recaptcha-box" style={{ display: builder == true ? 'inline-block' : 'none' }}>
          <div className="cf-recaptcha-placeholder" style={{ display: 'inline-block' }}>
            <svg width="100%" height="60" xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: 'Roboto, Arial, sans-serif' }}>
              <rect x="0" y="0" width="100%" height="60" fill="#f9f9f9" stroke="#dadce0" />
              <rect x="20" y="18" width="24" height="24" fill="white" stroke="#dadce0" rx="4" />
              <polyline points="26,30 32,36 42,24" fill="none" stroke="#34a853" strokeWidth="2.5" />
              <text x="60" y="36" fontSize="15" fill="#202124">I'm not a robot</text>
            </svg>
          </div>
        </div>
      )}
      <input
        className="cf-recaptcha-response-validation"
        id={`cf-form-recaptcha-response-${element.id}`}
        name="contact[recaptcha_response]"
      />
    </>
  );
});

const ElementForm = (props) => {
  let element = props.element;
  let options = element.options;
  let important = props.builder !== true ? " !important" : "";
  const classes = style.classes(element, props.builder, props.device);
  const stylesheet = buildFormStylesheet(
    props.builder,
    props.device,
    props.cta,
    element,
    props.variant
  );

  const customFields = Object.values(element.fields || {}).filter(
    (field) => field.toBeDeleted !== true
  );
  const { interleaved, trailing } = splitConsentPlacements(
    options,
    customFields
  );
  const trailingEnabled = trailing
    .filter((consent) => options[consent.data_name] == "true")
    .map((consent) => consent.data_name.replace("-consent", ""));
  const consent_visible =
    trailingEnabled.includes("marketing") && trailingEnabled.includes("privacy")
      ? "both"
      : trailingEnabled.join("-") || "false";

  const recaptchaBox = useRef(null);

  if (props.builder == true) {
    useEffect(() => {
      if (recaptchaBox.current && getWebsiteIntegration(props.website, "Googlerecaptcha") == undefined) {
        recaptchaBox.current.remove();
      }

      let form = document.querySelector(
        '#builder form[data-element-id="' + element.id + '"]'
      );
      $(form).on("submit", function (e) {
        e.preventDefault();
        return false;
      });
    });
  }

  const currentDevice = props.device;

  const formType = currentDevice == "mobile" && element.options["form-type-mobile"] ? element.options["form-type-mobile"] : element.options["form-type"];
  const buttonPlacementMobile =
    element.options["form-horizontal-button-placement-mobile"] ||
    (element.options["form-horizontal-button-placement"] == "below"
      ? "below"
      : "inline");
  const buttonPlacement =
    currentDevice == "mobile"
      ? buttonPlacementMobile
      : element.options["form-horizontal-button-placement"];

  return (
    <React.Fragment>
      <div
        id={"element" + element.id + "-content"}
        className="element-content cf-form"
      >
        <div
          id={"element" + element.id + "_form"}
          className={classes}
          data-element-id={element.id}
          data-consent={consent_visible}
          style={{
            fontFamily: options["field-font"]
              ? options["field-font"] + ", sans-serif" + important
              : undefined,
          }}
          data-form-type={options["form-type"]}
          data-form-type-mobile={options["form-type-mobile"]}
          data-font={element.options["field-label-font-family"]}
        >
          <form
            acceptCharset="UTF-8"
            data-element-id={element.id}
            element-id={element.id}
            data-remote="true"
            className="new_contact"
            method="post"
            noValidate="novalidate"
            data-hide-prefilled-fields={
              element.options["hide-prefilled-fields"]
            }
          >
            <input type="hidden" name="contact[visitor_token]" />
            <input type="hidden" name="contact[source_url]" />
            <input type="hidden" name="contact[contactable_type]" value="Cta" />
            <input
              type="hidden"
              name="contact[contactable_id]"
              value={props.cta.id}
            />
            <input
              type="hidden"
              name="contact[website_id]"
              value={element.website_id}
            />
            <input
              type="hidden"
              name="contact[element_id]"
              value={element.id}
            />
            <input
              type="hidden"
              id={"form" + element.id + "-broadcast-id"}
              name="contact[broadcast_id]"
            />
            <input
              type="hidden"
              id={"form" + element.id + "-variant-id"}
              name="contact[variant_id]"
              value={props.variant_id}
            />

            <div className="cf-form-container">
              <div className="cf-form-fields">
                <ElementFormPresetFields {...props} />
                <ElementFormCustomFields {...props} />
                {interleaved.length > 0 && (
                  <ElementFormConsentFields
                    element={element}
                    website={props.website}
                    builder={props.builder}
                    placement="inline"
                    consentsToRender={interleaved.map(
                      (consent) => consent.data_name
                    )}
                  />
                )}
              </div>

              <div
                className="cf-form-button cf-form-button-within-container"
                style={{ display: buttonPlacement !== "below" && formType == "horizontal" ? "flex" : "none" }}>
                <ElementFormButton
                  element={element}
                  builder={props.builder}
                  {...props}
                ></ElementFormButton>
              </div>
            </div>

            {trailing.length > 0 && (
            <div className="cf-form-consent">
              <ElementFormConsentFields
                element={element}
                website={props.website}
                builder={props.builder}
                consentsToRender={trailing.map(
                  (consent) => consent.data_name
                )}
              />
            </div>
            )}
            <RecaptchaDiv ref={recaptchaBox} builder={props.builder} element={element} website={props.website} />

            <div
              className="cf-form-button cf-form-button-outside-container"
              style={{ display: buttonPlacement == "below" && formType == "horizontal" ? "block" : formType !== "horizontal" ? "" : "none" }}
            >
              <ElementFormButton
                element={element}
                builder={props.builder}
                {...props}
              ></ElementFormButton>
            </div>
          </form>
        </div>
        <BuilderStyleTag
          builder={props.builder}
          styleKey={`cta:${props.cta.id}:element:${element.id}:form`}
          stylesheet={stylesheet}
        />
      </div>
    </React.Fragment>
  );
};

const style = {
  classes: (element, builder, device) => {
    let options = element.options;
    let classes =
      "cf-form cf-form-2 cf-form-" +
      (device == "mobile" && options["form-type-mobile"]
        ? options["form-type-mobile"]
        : options["form-type"]);

    classes +=
      " " +
      (options["form-theme"] ? options["form-theme"] : "cf-form-theme-default");

    classes += " " + (options["cf-alignment"] ? options["cf-alignment"] : "");

    let formWidthClass = "";
    if (options["form-width"] && options["form-width"] !== "custom") {
      formWidthClass = options["form-width"];
    }

    if (device == "mobile") {
      if (options["form-width-mobile"] && options["form-width-mobile"] !== "custom") {
        formWidthClass = options["form-width-mobile"];
      }
    }
    classes += " " + formWidthClass;

    if (!options["field-size"] || options["field-size"] == "cf-field-regular") {
      classes += " cf-field-regular";
    }

    if (options["field-size"] == "cf-field-large") {
      classes += " cf-field-large";
    }

    if (options["field-size"] == "custom") {
      classes += " cf-field-custom";
    }

    classes +=
      " " +
      (options["field-rounded"]
        ? options["field-rounded"]
        : "cf-field-slightly-rounded");
    return classes;
  },
};

export default ElementForm;
